import moment from 'moment-timezone';
import SDK from 'bookthatapp-sdk';
import axios from 'axios';
import { SHORT_DATE_FORMAT } from '../constants';

const sdk = SDK.init(window.bta.apiToken, { apiBaseUrl: window.bta.apiBaseUrl });

export const convertFramesToShopTimezone = (frames, timeZone) => {
  // frames returned from server is in UTC
  // we will re-arrange them into shop timezone
  const clientFrames = {};
  let i = 0;

  frames.forEach((f) => {
    f.timeslots.forEach((t) => {
      const timeslotDate = moment(t.start)
        .tz(timeZone)
        .format(SHORT_DATE_FORMAT);
      if (!clientFrames[timeslotDate]) {
        clientFrames[timeslotDate] = {
          id: i,
          year: moment(t.start)
            .tz(timeZone)
            .format('YYYY'),
          month: moment(t.start)
            .tz(timeZone)
            .format('MM'),
          date: moment(t.start)
            .tz(timeZone)
            .format(SHORT_DATE_FORMAT),
          timeslots: [],
        };
        i++;
      }

      clientFrames[timeslotDate].timeslots.push({
        ...t,
        date: moment(t.start)
          .tz(timeZone)
          .format(SHORT_DATE_FORMAT),
        start: t.start,
        finish: t.finish,
      });
    });
  });

  return Object.values(clientFrames);
};

export const flattenFramesToShopTimezone = (frames, timeZone) => {
  const arr = [];

  frames.forEach((frame) => {
    frame.timeslots.forEach((timeslot) => {
      arr.push({
        ...timeslot,
        start: moment(timeslot.start)
          .tz(timeZone)
          .toDate(),
        finish: moment(timeslot.finish)
          .tz(timeZone)
          .toDate(),
      });
    });
  });

  return arr;
};

export const getFrames = ({ external_id, start, finish }) => {
  const { timeZone } = window.bta;

  return sdk
    .getFrames({
      external_id,
      start,
      finish,
    })
    .then(({ data }) => Promise.resolve({
      ...data,
      frames: convertFramesToShopTimezone(data.frames, timeZone),
      flattenTimeSlots: flattenFramesToShopTimezone(data.frames, timeZone),
      lastOccurrence: data.last_occurrence
        ? moment(data.last_occurrence)
          .tz(timeZone)
          .format(SHORT_DATE_FORMAT)
        : null,
      nextOccurrence: data.next_occurrence
        ? moment(data.next_occurrence)
          .tz(timeZone)
          .format(SHORT_DATE_FORMAT)
        : null,
    }));
};

export const getCourseFrames = ({ external_id, start, finish }) => {
  const { timeZone } = window.bta;

  return sdk
    .getCourseFrames({
      external_id,
      start,
      finish,
    })
    .then(({ data }) => Promise.resolve({
      ...data,
      frames: convertFramesToShopTimezone(data.frames, timeZone),
      flattenTimeSlots: flattenFramesToShopTimezone(data.frames, timeZone),
      lastOccurrence: data.last_occurrence
        ? moment(data.last_occurrence)
          .tz(timeZone)
          .format(SHORT_DATE_FORMAT)
        : null,
      nextOccurrence: data.next_occurrence
        ? moment(data.next_occurrence)
          .tz(timeZone)
          .format(SHORT_DATE_FORMAT)
        : null,
    }));
};

/**
 * Get Bookings from startMin to finishMax.
 * @param {Date} startMin Start Date
 * @param {Date} finishMax Finish Date
 */
export const getBookings = (startMin, finishMax) => {
  const { timeZone } = window.bta;
  return sdk
    .getBookings({
      start_min: startMin
        ? moment(startMin)
          .tz(timeZone)
          .format(SHORT_DATE_FORMAT)
        : null,
      finish_max: finishMax
        ? moment(finishMax)
          .tz(timeZone)
          .format(SHORT_DATE_FORMAT)
        : null,
    }).then(data => Promise.resolve({
      ...data.data,
    }));
};

/**
 * Get blackouts from start_min to finish_max.
 * @param {Date} start_min get blackouts from
 * @param {Date} finish_max get blackouts to.
 */
export const getBlackouts = ({
  // eslint-disable-next-line camelcase
  start_min,
  // eslint-disable-next-line camelcase
  finish_max,
}) => sdk
  .getBlackouts({
    start_min,
    finish_max,
  })
  .then(({ data }) => Promise.resolve({
    ...data,
  }));

export const getBlocks = ({
  // eslint-disable-next-line camelcase
  external_id, start, finish, location_ids, resource_ids, interval,
}) => sdk
  .getBlocks({
    external_id,
    start,
    finish,
    location_ids,
    resource_ids,
    interval,
  })
  .then(({ data }) => Promise.resolve({
    ...data,
  }));

export const createReservation = (items, retry) => sdk.createReservation({
  cart_token: window.xprops.cartToken,
  items,
  retry
});

export const getProducts = () => sdk.getProducts()
  .then(response => response.data)
  .catch((error) => { throw error; });

export const getSchedules = (params) => {
  if (window.xprops && window.xprops.appUrl) {
    const { appUrl } = window.xprops;
    const url = new URL(appUrl);
    const apiUrl = `${url.protocol}//${url.host}/availability/schedule.json`;
    return axios.get(apiUrl, { params })
      .then(resp => (resp.data))
      .catch((error) => {
        throw error;
      });
  }
  return Promise.reject(new Error('getSchedules - no window.xprops...'));
};

export const getCalendarEvents = (params) => {
  if (window.xprops && window.xprops.appUrl) {
    const { appUrl } = window.xprops;
    const url = new URL(appUrl);
    const apiUrl = `${url.protocol}//${url.host}/calendar/events.json`;
    return axios.get(apiUrl, { params })
      .then(resp => (resp.data))
      .catch((error) => {
        throw error;
      });
  }
  return Promise.reject(new Error('getCalendarEvents - no window.xprops...'));
};

export const getUpcomingEventsProducts = (params) => {
  if (window.xprops && window.xprops.appUrl) {
    const { appUrl } = window.xprops;
    const url = new URL(appUrl);
    const apiUrl = `${url.protocol}//${url.host}/widgets/upcoming_events_products`;

    return axios.get(apiUrl, { params: {
        start: moment().utc()
          .add(15, 'minute')
          .format('YYYY-MM-DDTHH:mm'),
        finish: moment().utc()
          .add(5, 'month')
          .format('YYYY-MM-DDTHH:mm'),
      }})
      .then(resp => (resp.data))
      .catch((error) => {
        throw error;
      });
  }
  return Promise.reject(new Error('getUpcomingEventsProducts - no window.xprops...'));
};

export default {
  getFrames,
  getBlocks,
  getCourseFrames,
  createReservation,
  getProducts,
  getSchedules,
};
